import { type ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { parseLiveInputStatusUpdateMessage } from '@/services/inputStatus/live-input-status.service';
import { applicationDataSelector } from '@/store/slices/application-data';

export type InputLiveStatusSnapshot = {
  inputId: string;
  overallStatus?: string;
  liveProgramIds: string[];
};

type LiveStatusListener = (status: InputLiveStatusSnapshot) => void;

const statusByInputId = new Map<string, InputLiveStatusSnapshot>();
const listenersByInputId = new Map<string, Set<LiveStatusListener>>();
const inflightByInputId = new Map<string, Promise<InputLiveStatusSnapshot>>();

const readOverallStatus = (data?: Record<string, unknown> | null): string | undefined => {
  if (!data) {
    return undefined;
  }

  return (data.overallStatus ?? data.OverallStatus ?? data.status) as string | undefined;
};

const readLiveProgramIds = (data?: Record<string, unknown> | null): string[] => {
  if (!data || !Array.isArray(data.liveProgramIds)) {
    return [];
  }

  return data.liveProgramIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
};

const notifyListeners = (status: InputLiveStatusSnapshot) => {
  statusByInputId.set(status.inputId, status);
  listenersByInputId.get(status.inputId)?.forEach((listener) => listener(status));
};

const isUnassignedInputId = (inputId: string | undefined, ouId?: string): boolean => Boolean(inputId && ouId && inputId === ouId);

const toSubscribedIds = (inputIds: string[], ouId?: string): string[] =>
  Array.from(new Set(inputIds.filter((inputId) => inputId && !isUnassignedInputId(inputId, ouId)))).sort();

export const fetchInputLiveStatusSnapshot = async (inputId: string): Promise<InputLiveStatusSnapshot> => {
  const existing = inflightByInputId.get(inputId);
  if (existing) {
    return existing;
  }

  const request = (async () => {
    const response = (await FuelToolsApiService.getInputStreamStatus(inputId)) as Record<string, unknown> | null;
    const snapshot: InputLiveStatusSnapshot = {
      inputId,
      overallStatus: readOverallStatus(response ?? undefined),
      liveProgramIds: readLiveProgramIds(response ?? undefined),
    };

    notifyListeners(snapshot);
    return snapshot;
  })();

  inflightByInputId.set(inputId, request);
  try {
    return await request;
  } finally {
    inflightByInputId.delete(inputId);
  }
};

export const subscribeToInputLiveStatus = (inputId: string, listener: LiveStatusListener) => {
  if (!listenersByInputId.has(inputId)) {
    listenersByInputId.set(inputId, new Set());
  }

  listenersByInputId.get(inputId)?.add(listener);

  const current = statusByInputId.get(inputId);
  if (current) {
    listener(current);
  }

  return () => {
    listenersByInputId.get(inputId)?.delete(listener);
  };
};

const refreshInputLiveStatus = (inputId: string) => {
  fetchInputLiveStatusSnapshot(inputId).catch((error) => {
    console.error('Failed to fetch input live status', { inputId, error });
  });
};

/** Mount once per live-broadcast page to listen for WebSocket status notifications. */
export const InputSourceLiveStatusProvider = ({ children }: { children: ReactNode }) => {
  const { lastJsonMessage } = useWebSocketContext();
  const { ouData } = useSelector(applicationDataSelector);
  const ouId = ouData?.ouId;

  useEffect(() => {
    if (lastJsonMessage == null) {
      return;
    }

    const payload = parseLiveInputStatusUpdateMessage(lastJsonMessage);
    if (!payload?.inputId || isUnassignedInputId(payload.inputId, ouId) || !listenersByInputId.get(payload.inputId)?.size) {
      return;
    }

    refreshInputLiveStatus(payload.inputId);
  }, [lastJsonMessage, ouId]);

  return children;
};

/** Page-level subscribe + fetch + visibility refetch for a set of input ids. */
export const useInputSourceLiveStatus = (inputIds: string[]) => {
  const { ouData } = useSelector(applicationDataSelector);
  const subscribedIds = toSubscribedIds(inputIds, ouData?.ouId);
  const subscribedKey = subscribedIds.join(',');
  const [liveStatusByInputId, setLiveStatusByInputId] = useState<Record<string, InputLiveStatusSnapshot>>({});

  useEffect(() => {
    const ids = subscribedKey ? subscribedKey.split(',') : [];
    const unsubscribers = ids.map((id) =>
      subscribeToInputLiveStatus(id, (status) => {
        setLiveStatusByInputId((prev) => ({ ...prev, [status.inputId]: status }));
      })
    );

    ids.forEach((id) => {
      refreshInputLiveStatus(id);
    });

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      ids.forEach((id) => {
        refreshInputLiveStatus(id);
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [subscribedKey]);

  return {
    liveStatusByInputId,
    liveStatus: subscribedIds.length === 1 ? liveStatusByInputId[subscribedIds[0]] ?? null : null,
  };
};

/** Subscribe to cached live status without fetching. Use from portals on a page that already subscribed. */
export const useObservedInputLiveStatus = (inputId?: string): InputLiveStatusSnapshot | null => {
  const { ouData } = useSelector(applicationDataSelector);
  const ouId = ouData?.ouId;
  const observedInputId = isUnassignedInputId(inputId, ouId) ? undefined : inputId;
  const [liveStatus, setLiveStatus] = useState<InputLiveStatusSnapshot | null>(() =>
    observedInputId ? statusByInputId.get(observedInputId) ?? null : null
  );

  useEffect(() => {
    if (!observedInputId) {
      setLiveStatus(null);
      return undefined;
    }

    return subscribeToInputLiveStatus(observedInputId, setLiveStatus);
  }, [observedInputId]);

  return liveStatus;
};
